<template>
  <div v-if="showModule" class="target-hours-module">
    <div class="target-hours-container">
      <div class="target-hours-content" @wheel.stop>
        <div v-if="loading" class="target-hours-loading">
          <ProgressSpinner />
        </div>

        <div v-else-if="targetHours.length === 0" class="target-hours-empty">
          <EmptyState
            :title="t('wkt.client.target-hours.empty-state.title')"
            :description="t('wkt.client.target-hours.empty-state.description')"
          />
        </div>

        <template v-else>
          <DataTable
            v-if="!isMobile"
            :value="targetHours"
            stripedRows
            class="target-hours-table"
            :tableStyle="{ tableLayout: 'fixed' }"
          >
            <Column
              field="name"
              :header="t('wkt.client.target-hours.name')"
              headerStyle="width: 22%"
              bodyStyle="width: 22%"
            />

            <Column
              :header="t('wkt.client.target-hours.start')"
              headerStyle="width: 26%"
              bodyStyle="width: 26%"
            >
              <template #body="{ data }">
                <div class="target-hours-period">
                  <span>{{ formatSimpleDate(data.startDate) }}</span>
                  <span>{{ formatSimpleDate(data.endDate) }}</span>
                </div>
              </template>
            </Column>

            <Column
              headerStyle="width: 18%; text-align: right"
              bodyStyle="width: 18%; text-align: right"
              bodyClass="target-hours-cell-right"
            >
              <template #header>
                <div class="target-hours-column-header target-hours-column-header-right">
                  {{ t('wkt.client.target-hours.target') }}
                </div>
              </template>

              <template #body="{ data }">
                <span class="target-hours-number">
                  {{ formatRoundedHours(data.targetHours) }}
                </span>
              </template>
            </Column>

            <Column
              headerStyle="width: 24%; text-align: right"
              bodyStyle="width: 24%; text-align: right"
              bodyClass="target-hours-cell-right"
            >
              <template #header>
                <div class="target-hours-column-header target-hours-column-header-right">
                  {{ t('wkt.client.target-hours.worked') }}
                </div>
              </template>

              <template #body="{ data }">
                <span
                  :class="['target-hours-balance', getBalanceClass(data.balance)]"
                >
                  {{ formatWorkedAndBalance(data) }}
                </span>
              </template>
            </Column>
          </DataTable>

          <div v-else class="target-hours-cards">
            <Card
              v-for="item in targetHours"
              :key="item.id"
              class="target-hours-card"
            >
              <template #title>
                <span class="target-hours-card-title">{{ item.name }}</span>
              </template>

              <template #content>
                <div class="target-hours-card-grid">
                  <div class="target-hours-card-row">
                    <span class="target-hours-card-label">
                      {{ t('wkt.client.target-hours.start') }}
                    </span>
                    <span class="target-hours-card-value">
                      {{ formatPeriod(item) }}
                    </span>
                  </div>

                  <div class="target-hours-card-row">
                    <span class="target-hours-card-label">
                      {{ t('wkt.client.target-hours.target') }}
                    </span>
                    <span class="target-hours-card-value">
                      {{ formatRoundedHours(item.targetHours) }}
                    </span>
                  </div>

                  <div class="target-hours-card-row">
                    <span class="target-hours-card-label">
                      {{ t('wkt.client.target-hours.worked') }}
                    </span>
                    <span
                      :class="['target-hours-card-value', getBalanceClass(item.balance)]"
                    >
                      {{ formatWorkedAndBalance(item) }}
                    </span>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { FullTargetHoursDTO } from "@wkt/api/targetHoursDTO";
import { worktimeApi } from "@wkt/api/worktime";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useAlert } from "@wkt/composables/useAlert";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";

const { t, locale } = useI18n();
const { showAlert } = useAlert();
const { formatSimpleDate } = useDateFormatter();

const showModule = ref(false);
const loading = ref(false);
const isMobile = ref(false);
const targetHours = ref<FullTargetHoursDTO[]>([]);

function updateViewport(): void {
  isMobile.value = window.innerWidth < 768;
}

function isTargetHoursList(payload: unknown): payload is FullTargetHoursDTO[] {
  return Array.isArray(payload) && payload.every((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const candidate = item as Partial<FullTargetHoursDTO>;
    return typeof candidate.id === "number" && typeof candidate.name === "string";
  });
}

function formatRoundedHours(value?: number | null): string {
  return new Intl.NumberFormat([locale.value, "en"], {
    maximumFractionDigits: 0,
  }).format(value ?? 0);
}

function formatWorkedAndBalance(item: FullTargetHoursDTO): string {
  return `${formatRoundedHours(item.workedHours)} / ${formatRoundedHours(Math.abs(item.balance ?? 0))}`;
}

function formatPeriod(item: FullTargetHoursDTO): string {
  return `${formatSimpleDate(item.startDate)} - ${formatSimpleDate(item.endDate)}`;
}

function getBalanceClass(balance?: number | null): string {
  if (balance === null || balance === undefined) {
    return "target-hours-balance-neutral";
  }

  return balance < 0
    ? "target-hours-balance-positive"
    : "target-hours-balance-negative";
}

function applyPayload(payload: FullTargetHoursDTO[]): void {
  targetHours.value = payload;
  loading.value = false;
}

function loadTargetHours(): void {
  loading.value = true;

  worktimeApi.getTargetHoursList(
    (response) => {
      targetHours.value = response ?? [];
      loading.value = false;
    },
    (error) => {
      console.error("Error loading target hours:", error);
      targetHours.value = [];
      loading.value = false;
      showAlert({
        text: t("wkt.client.common.error.try_again_later"),
        color: "error",
      });
    }
  );
}

function onReveal(payload?: unknown): void {
  showModule.value = true;

  if (isTargetHoursList(payload)) {
    applyPayload(payload);
    return;
  }

  loadTargetHours();
}

function refreshModule(): void {
  showModule.value = true;
  loadTargetHours();
}

onMounted(() => {
  updateViewport();
  window.addEventListener("resize", updateViewport);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateViewport);
});

defineExpose({
  open: onReveal,
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.target-hours-module {
  width: 100%;
  min-height: calc(100vh - 120px);
  padding: 0 0.75rem 2rem;
  box-sizing: border-box;
}

.target-hours-container {
  width: 100%;
}

.target-hours-content {
  max-width: 1120px;
  margin: 0 auto;
}

.target-hours-loading,
.target-hours-empty {
  min-height: 45vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.target-hours-period {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.target-hours-column-header {
  width: 100%;
}

.target-hours-column-header-right {
  text-align: right;
}

.target-hours-table :deep(.target-hours-cell-right) {
  text-align: right;
}

.target-hours-number {
  display: inline-block;
  width: 100%;
  text-align: right;
}

.target-hours-balance {
  display: inline-block;
  width: 100%;
  text-align: right;
  font-weight: 600;
}

.target-hours-balance-positive {
  color: #2e7d32;
}

.target-hours-balance-negative {
  color: #c62828;
}

.target-hours-balance-neutral {
  color: var(--grey-text);
}

.target-hours-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.target-hours-card {
  border: 1px solid var(--border-color);
  box-shadow: none;
}

.target-hours-card-title {
  font-size: 1.05rem;
  font-weight: 600;
}

.target-hours-card-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.target-hours-card-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.target-hours-card-label {
  font-weight: 600;
  color: var(--black);
}

.target-hours-card-value {
  text-align: right;
  color: var(--grey-text);
}

@media (max-width: 768px) {
  .target-hours-module {
    min-height: calc(100vh - 140px);
    padding: 0 0.5rem 1rem;
  }
}
</style>
